import React, { useState, useMemo, useContext, Fragment } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { AlertTypes, Button, InfoList, InfoListData, InputTextarea } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useAddComment } from '@/generated/application-registrations';
import { AccountId, RegistrationCompositeDetailsResponse, RegistrationCompositeId } from '@/generated/data.schemas';
import { getGetRegistrationCompositeQueryKey } from '@/generated/registration-composite';
import { useGetTranslations } from '@/generated/translations';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './ApplicationDetail.module.scss';
import { EditApplication } from './EditApplication';
import { RegisterApplication } from './RegisterApplication';

interface ApplicationOverviewProps {
	accountId: AccountId;
	registrationCompositeId: RegistrationCompositeId;
	application?: RegistrationCompositeDetailsResponse;
}

export const ApplicationOverview: React.FunctionComponent<ApplicationOverviewProps> = ({
	application,
	accountId,
	registrationCompositeId,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const [hasChanged, setHasChanged] = useState(false);
	const queryClient = useQueryClient();
	const addNotification = useNotificationStore((state) => state.addNotification);

	const applicationInfo: InfoListData = useMemo(() => {
		const {
			name = '',
			segmentName = '',
			applicationName,
			applicationSystemName = '',
			brand = '',
			model = '',
			fluidType = '',
			fluidBrand = '',
			oilSystem = '',
			oilSystemBrand = '',
			powerSupply = '',
			viscosity = '',
			systemVolume = '',
			minAmbientTemperature = '',
			maxAmbientTemperature = '',
			minOperationTemperature = '',
			maxOperationTemperature = '',
			normalOperationTemperature = '',
			additionalInformation = '',
		} = application || {};
		return {
			name, // user-selected name for the application
			segment: segmentName,
			application: applicationName ?? applicationSystemName,
			brand,
			model,
			oilSystem,
			oilSystemBrand,
			fluidBrand,
			fluidType,
			viscosity: (viscosity !== null && viscosity !== '') ? (
				<Fragment>
					{viscosity}
					<span className={styles.suffix}>{translations?.data.applications.viscosityUnit}</span>
				</Fragment>
			) : null,
			systemVolume: (systemVolume !== null && systemVolume !== '') ? (
				<Fragment>
					{systemVolume}
					<span className={styles.suffix}>{translations?.data.applications.volumeUnit}</span>
				</Fragment>
			) : null,
			minAmbientTemperature: (minAmbientTemperature !== null && minAmbientTemperature !== '') ? (
				<Fragment>
					{minAmbientTemperature}
					<span className={styles.suffix}>{translations?.data.applications.temperatureUnit}</span>
				</Fragment>
			) : null,
			maxAmbientTemperature: (maxAmbientTemperature !== null && maxAmbientTemperature !== '') ? (
				<Fragment>
					{maxAmbientTemperature}
					<span className={styles.suffix}>{translations?.data.applications.temperatureUnit}</span>
				</Fragment>
			) : null,
			minOperationTemperature: (minOperationTemperature !== null && minOperationTemperature !== '') ? (
				<Fragment>
					{minOperationTemperature}
					<span className={styles.suffix}>{translations?.data.applications.temperatureUnit}</span>
				</Fragment>
			) : null,
			maxOperationTemperature: (maxOperationTemperature !== null && maxOperationTemperature !== '') ? (
				<Fragment>
					{maxOperationTemperature}
					<span className={styles.suffix}>{translations?.data.applications.temperatureUnit}</span>
				</Fragment>
			) : null,
			normalOperationTemperature: (normalOperationTemperature !== null && normalOperationTemperature !== '') ? (
				<Fragment>
					{normalOperationTemperature}
					<span className={styles.suffix}>{translations?.data.applications.temperatureUnit}</span>
				</Fragment>
			) : null,
			powerSupply,
			additionalInformation,
		};
	}, [application, translations]);
	const addCommentMutation = useAddComment({
		mutation: {
			onSuccess: async () => {
				// refresh application details
				if (application?.id)
					await queryClient.invalidateQueries({
						queryKey: getGetRegistrationCompositeQueryKey(
							segmentationId,
							accountId,
							registrationCompositeId,
						),
					});
				addNotification({
					children: translations?.data.applications.saveCommentSuccess,
					type: AlertTypes.SUCCESS,
				});
			},
			onError: () => {
				addNotification({
					children: translations?.data.applications.saveCommentFailed,
					type: AlertTypes.DANGER,
				});
			},
		},
	});

	// set has input text changed state - used for disabling the save button
	const handleCommentChange = (): void => {
		if (hasChanged) return;
		setHasChanged(true);
	};

	const submitComment = (event) => {
		event.preventDefault();

		const formData = new FormData(event.target);

		addCommentMutation.mutate({
			segmentationId: Number(segmentationId),
			accountId: Number(accountId),
			registrationCompositeId,
			applicationId: Number(application?.id),
			data: { comment: String(formData.get('Comment')) },
		});

		event.target.reset();
	};
	return (
		<section>
			<div className={styles.section}>
				<header className={classNames(styles.sectionHeader, 'u-mb-md')}>
					<h2 className={styles.sectionHeadline}>{translations?.data.applications.applicationInformation}</h2>

					{application?.id ? (
						<EditApplication
							accountId={accountId}
							registrationCompositeId={registrationCompositeId}
							application={application}
						/>
					) : (
						<RegisterApplication accountId={accountId} registrationCompositeId={registrationCompositeId} />
					)}
				</header>
				<InfoList
					data={applicationInfo}
					translations={translations?.data.applications}
					listOnly
					filterEmptyValues
				/>
			</div>
			<hr />
			<div className={styles.section}>
				<div className={styles.sectionHeader}>
					<h2 className={styles.sectionHeadline}>{translations?.data.applications.comments}</h2>
				</div>

				{application?.comments
					.filter((comment) => comment.comment.length > 0)
					.map((comment) => (
						<div key={comment.id} className={styles.comment}>
							{comment.comment}
						</div>
					))}

				<form id="commentForm" onSubmit={submitComment} className={styles.commentForm}>
					<fieldset>
						<legend className={'u-hide-visually'}>{translations?.data.applications.addComment}</legend>
						<InputTextarea
							name="Comment"
							id="Comment"
							rows={4}
							label={translations?.data.applications.addComment}
							onChange={handleCommentChange}
							variant={'inverse'}
						></InputTextarea>
						<div className={styles.commentBtn}>
							<Button size="sm" type="submit" disabled={!hasChanged}>
								{translations?.data.applications.saveComment}
							</Button>
						</div>
					</fieldset>
				</form>
			</div>
		</section>
	);
};
